import DOMPurify from 'dompurify';

import {
  ABOUT_ALLOWED_TAGS,
  ABOUT_FORBID_TAGS,
  ABOUT_MAX_LENGTH,
  isAllowedAboutStyle,
} from 'constants/about';

const looksLikeHtml = (value: string): boolean => /<[a-z][\s\S]*>/i.test(value);

let domPurifyHookInstalled = false;

const ensureDomPurifyHooks = () => {
  if (domPurifyHookInstalled || typeof window === 'undefined') return;
  domPurifyHookInstalled = true;

  DOMPurify.addHook('uponSanitizeAttribute', (node, data) => {
    if (data.attrName !== 'style') return;
    if (!isAllowedAboutStyle(data.attrValue)) {
      data.keepAttr = false;
      return;
    }
    if (node.tagName && !['P', 'H1', 'H2', 'H3'].includes(node.tagName)) {
      data.keepAttr = false;
    }
  });
};

export const getAboutPlainText = (value: string): string => {
  if (!value?.trim()) return '';
  if (!looksLikeHtml(value)) return value;
  const doc = new DOMParser().parseFromString(value, 'text/html');
  return doc.body.textContent ?? '';
};

export const getAboutPlainTextLength = (value: string): number => getAboutPlainText(value).length;

export const sanitizeAboutHtml = (value: string): string => {
  const trimmed = value?.trim() ?? '';
  if (!trimmed) return '';

  if (!looksLikeHtml(trimmed)) {
    const escaped = trimmed
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
    const paragraphs = escaped.split('\n').map((part) => part.trim()).filter(Boolean);
    if (!paragraphs.length) return '';
    return paragraphs.map((part) => `<p>${part}</p>`).join('');
  }

  ensureDomPurifyHooks();

  const clean = DOMPurify.sanitize(trimmed, {
    ALLOWED_TAGS: [...ABOUT_ALLOWED_TAGS],
    ALLOWED_ATTR: ['style'],
    FORBID_TAGS: [...ABOUT_FORBID_TAGS],
    KEEP_CONTENT: true,
  });

  if (!getAboutPlainText(clean).trim()) return '';
  return clean;
};

export const normalizeAboutForStorage = (value: string): string => sanitizeAboutHtml(value);

export const isAboutWithinLimit = (value: string): boolean =>
  getAboutPlainTextLength(value) <= ABOUT_MAX_LENGTH;

export const hasAboutContent = (value?: string | null): boolean =>
  Boolean(getAboutPlainText(value ?? '').trim());

export { ABOUT_MAX_LENGTH };
